import React, { useState, useCallback, useRef, memo } from 'react';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  ReactFlowProvider,
  MarkerType,
  Handle,
  Position,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
  Box,
  Container,
  Title,
  Text,
  Stack,
  Button,
  Paper,
  Group,
  Alert,
  Badge,
  Tabs,
  Table,
  ThemeIcon,
  Loader,
  ScrollArea,
  Divider,
} from '@mantine/core';
import {
  IconCode,
  IconUpload,
  IconAlertCircle,
  IconInfoCircle,
  IconChartDots,
  IconList,
  IconVariable,
  IconSettings,
  IconCheck,
  IconFileCode,
  IconDownload,
  IconRefresh,
  IconFiles,
  IconCircleCheck,
  IconCircleX,
  IconClockHour4,
  IconBuildingStore,
  IconComponents,
  IconNetwork,
  IconX,
} from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import PlatformNavBar from '../../components/PlatformNavBar';

const API_BASE = process.env.REACT_APP_CCUTILS_API_BASE_URL || '';

// ============================================================================
// Node type color schemes
// ============================================================================

const NODE_COLORS = {
  start:    { border: '#2e7d32', bg: '#e8f5e9', text: '#1b5e20', label: 'START' },
  decision: { border: '#e65100', bg: '#fff3e0', text: '#bf360c', label: 'DECISION' },
  menu:     { border: '#1565c0', bg: '#e3f2fd', text: '#0d47a1', label: 'MENU' },
  queue:    { border: '#00695c', bg: '#e0f2f1', text: '#004d40', label: 'QUEUE' },
  action:   { border: '#37474f', bg: '#f5f5f5', text: '#263238', label: 'ACTION' },
  voicemail:{ border: '#6a1b9a', bg: '#f3e5f5', text: '#4a148c', label: 'VOICEMAIL' },
  email:    { border: '#0277bd', bg: '#e1f5fe', text: '#01579b', label: 'EMAIL' },
  end:      { border: '#b71c1c', bg: '#ffebee', text: '#b71c1c', label: 'END' },
};

// ============================================================================
// Custom React Flow node component
// ============================================================================

const UccxNode = memo(({ data }) => {
  const colors = NODE_COLORS[data.nodeType] || NODE_COLORS.action;
  return (
    <div
      style={{
        border: `2px solid ${colors.border}`,
        borderRadius: 8,
        background: colors.bg,
        padding: '10px 14px',
        minWidth: 210,
        maxWidth: 240,
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
        cursor: 'default',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: colors.border, width: 8, height: 8 }} />
      <div style={{ marginBottom: 4 }}>
        <span
          style={{
            display: 'inline-block',
            fontSize: 9,
            fontWeight: 700,
            color: colors.border,
            background: 'white',
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            padding: '1px 5px',
            marginBottom: 4,
            letterSpacing: '0.5px',
          }}
        >
          {colors.label}
        </span>
      </div>
      <div style={{ fontWeight: 700, color: colors.text, fontSize: 12, marginBottom: 3, lineHeight: 1.3 }}>
        {data.step != null && (
          <span style={{ marginRight: 5, opacity: 0.6, fontWeight: 400 }}>{data.step}.</span>
        )}
        {data.title}
      </div>
      {data.description && (
        <div style={{ color: '#555', fontSize: 11, lineHeight: 1.4, borderTop: `1px solid ${colors.border}22`, paddingTop: 4 }}>
          {data.description.length > 100 ? data.description.substring(0, 97) + '…' : data.description}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: colors.border, width: 8, height: 8 }} />
    </div>
  );
});

const nodeTypes = { uccxNode: UccxNode };

// ============================================================================
// Dagre auto-layout
// ============================================================================

const NODE_W = 244;
const NODE_H_BASE = 70; // minimum node height

/**
 * Estimate rendered node height from content — mirrors the architecture doc's
 * "Node Measurement: CRUCIAL STEP" pattern without requiring off-screen DOM rendering.
 */
function estimateNodeHeight(title, description) {
  const titleCharsPerLine = 26; // ~26 chars fit in 244px at 12px bold
  const titleLines = Math.max(1, Math.ceil((title?.length || 0) / titleCharsPerLine));
  const descCharsPerLine = 34;
  const truncated = description ? description.substring(0, 100) : '';
  const descLines = truncated.length > 0 ? Math.ceil(truncated.length / descCharsPerLine) : 0;
  // Badge row (20) + padding (20) + title lines (16px each) + desc separator (descLines > 0 ? 4 : 0) + desc lines (13px each)
  return Math.max(NODE_H_BASE, 40 + titleLines * 16 + (descLines > 0 ? 4 + descLines * 13 : 0));
}

/**
 * Post-layout collision resolution — simplified version of the architecture doc's
 * detectAndResolveCollisions() with up to 3 iterations.
 */
function resolveCollisions(nodes, nodeHeights) {
  const MARGIN_X = 20;
  const MARGIN_Y = 16;
  let laid = nodes.map(n => ({ ...n }));
  for (let iter = 0; iter < 3; iter++) {
    let moved = false;
    for (let i = 0; i < laid.length; i++) {
      for (let j = i + 1; j < laid.length; j++) {
        const a = laid[i];
        const b = laid[j];
        const ah = nodeHeights[a.id] || NODE_H_BASE;
        const bh = nodeHeights[b.id] || NODE_H_BASE;
        const overlapX = (NODE_W + MARGIN_X) - Math.abs(a.position.x - b.position.x);
        const overlapY = ((ah + bh) / 2 + MARGIN_Y) - Math.abs(a.position.y - b.position.y);
        if (overlapX > 0 && overlapY > 0) {
          const nudge = overlapY / 2 + 1;
          if (a.position.y <= b.position.y) {
            laid[i] = { ...a, position: { ...a.position, y: a.position.y - nudge } };
            laid[j] = { ...b, position: { ...b.position, y: b.position.y + nudge } };
          } else {
            laid[i] = { ...a, position: { ...a.position, y: a.position.y + nudge } };
            laid[j] = { ...b, position: { ...b.position, y: b.position.y - nudge } };
          }
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return laid;
}

/**
 * Layout with Dagre + complexity-aware settings (mirrors architecture doc's
 * LARGE_GRAPH / PERFORMANCE / QUALITY preset selection).
 */
function getLayoutedElements(nodes, edges, direction = 'TB', nodeHeights = {}) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));

  // Scale spacing based on graph complexity
  const n = nodes.length;
  const ranksep = n > 20 ? 100 : n > 10 ? 80 : 65;
  const nodesep = n > 20 ? 55 : n > 10 ? 45 : 36;
  g.setGraph({ rankdir: direction, ranksep, nodesep });

  nodes.forEach(n => {
    const h = nodeHeights[n.id] || NODE_H_BASE;
    g.setNode(n.id, { width: NODE_W, height: h });
  });
  edges.forEach(e => g.setEdge(e.source, e.target));

  dagre.layout(g);

  const layouted = nodes.map(n => {
    const pos = g.node(n.id);
    const h = nodeHeights[n.id] || NODE_H_BASE;
    return { ...n, position: { x: pos.x - NODE_W / 2, y: pos.y - h / 2 }, targetPosition: 'top', sourcePosition: 'bottom' };
  });

  return {
    nodes: resolveCollisions(layouted, nodeHeights),
    edges,
  };
}

// ============================================================================
// Build React Flow graph from AI analysis
// ============================================================================

function buildFlowGraph(analysis) {
  const rawNodes = [];
  const edges = [];
  const nodeHeights = {};
  const steps = analysis.call_flow_steps || [];
  const menuOptions = analysis.menu_options || [];

  // Find menu step index (if any)
  const menuStepIndex = steps.findIndex(s => s.node_type === 'menu');

  // Create a node for each call flow step — measure height from content
  steps.forEach(step => {
    const id = `step-${step.step}`;
    nodeHeights[id] = estimateNodeHeight(step.title, step.description);
    rawNodes.push({
      id,
      type: 'uccxNode',
      data: { step: step.step, title: step.title, description: step.description, nodeType: step.node_type },
      position: { x: 0, y: 0 },
    });
  });

  // Sequential edges between main steps
  for (let i = 0; i < steps.length - 1; i++) {
    const isFromMenu = i === menuStepIndex && menuOptions.length > 0;
    edges.push({
      id: `e-${steps[i].step}-${steps[i + 1].step}`,
      source: `step-${steps[i].step}`,
      target: `step-${steps[i + 1].step}`,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed, color: '#666' },
      style: { stroke: isFromMenu ? '#999' : '#555', strokeDasharray: isFromMenu ? '6,3' : undefined },
      label: isFromMenu ? 'overflow' : undefined,
      labelStyle: { fontSize: 10, fill: '#888' },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.85 },
      labelBgPadding: [3, 4],
    });
  }

  // Fan-out from menu step to each menu option
  if (menuStepIndex >= 0 && menuOptions.length > 0) {
    const menuStep = steps[menuStepIndex];
    menuOptions.forEach((opt, i) => {
      const optId = `menu-opt-${i}`;
      const title = `${opt.digit ? opt.digit + ': ' : ''}${opt.department}`;
      nodeHeights[optId] = estimateNodeHeight(title, opt.prompt_variable);
      rawNodes.push({
        id: optId,
        type: 'uccxNode',
        data: {
          title,
          description: opt.prompt_variable || '',
          nodeType: 'action',
        },
        position: { x: 0, y: 0 },
      });
      edges.push({
        id: `e-menu-${i}`,
        source: `step-${menuStep.step}`,
        target: optId,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed, color: '#1565c0' },
        label: opt.digit ? `Press ${opt.digit}` : undefined,
        labelStyle: { fontSize: 10, fill: '#1565c0', fontWeight: 600 },
        labelBgStyle: { fill: '#e3f2fd', fillOpacity: 0.9 },
        labelBgPadding: [3, 4],
        style: { stroke: '#1565c0' },
      });
    });
  }

  return getLayoutedElements(rawNodes, edges, 'TB', nodeHeights);
}

// ============================================================================
// Extract printable strings from binary ArrayBuffer (like Unix `strings`)
// ============================================================================

function extractStrings(arrayBuffer, minLen = 4) {
  const bytes = new Uint8Array(arrayBuffer);
  const results = [];
  let current = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b >= 0x20 && b <= 0x7e) {
      current += String.fromCharCode(b);
    } else {
      if (current.length >= minLen) results.push(current);
      current = '';
    }
  }
  if (current.length >= minLen) results.push(current);

  // Deduplicate, filter obvious noise, join
  const seen = new Set();
  return results
    .filter(s => {
      const t = s.trim();
      if (t.length < minLen) return false;
      if (seen.has(t)) return false;
      seen.add(t);
      return true;
    })
    .join('\n');
}

// ============================================================================
// Feature badge colors
// ============================================================================

const FEATURE_COLORS = [
  'teal', 'blue', 'violet', 'orange', 'green', 'cyan', 'indigo', 'pink',
];

// ============================================================================
// ScriptFlowDiagram — self-contained diagram for batch mode (own provider)
// ============================================================================

function ScriptFlowDiagramInner({ nodes: initialNodes, edges: initialEdges, filename }) {
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);
  const [locked, setLocked] = useState(true);
  const [exporting, setExporting] = useState(false);
  const diagramRef = useRef(null);
  const { fitView } = useReactFlow();

  // Fit view on first render — ensures diagram fills the viewport after layout (architecture doc §6 "Layout Application")
  const onInit = useCallback(() => {
    setTimeout(() => fitView({ padding: 0.15, duration: 300 }), 50);
  }, [fitView]);

  const exportPng = useCallback(async () => {
    if (!diagramRef.current) return;
    setExporting(true);
    try {
      const { toPng } = await import('html-to-image');
      const padding = 40;
      const el = diagramRef.current;
      const viewport = el.querySelector('.react-flow__viewport');
      if (!viewport) return;

      const nodeEls = el.querySelectorAll('.react-flow__node');
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      nodeEls.forEach(n => {
        const r = n.getBoundingClientRect();
        const p = el.getBoundingClientRect();
        const x = r.left - p.left; const y = r.top - p.top;
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + r.width); maxY = Math.max(maxY, y + r.height);
      });

      const w = maxX - minX + padding * 2;
      const h = maxY - minY + padding * 2;
      const origT = viewport.style.transform;
      const origW = el.style.width; const origH = el.style.height; const origO = el.style.overflow;

      el.style.width = `${w}px`; el.style.height = `${h}px`; el.style.overflow = 'visible';
      viewport.style.transform = `translate(${-minX + padding}px, ${-minY + padding}px) scale(1)`;
      await new Promise(r => setTimeout(r, 500));

      const dataUrl = await toPng(el, { pixelRatio: 2, backgroundColor: '#ffffff', width: w, height: h, cacheBust: true,
        filter: n => !n.classList?.contains('react-flow__controls') && !n.classList?.contains('react-flow__minimap') && !n.classList?.contains('react-flow__attribution') });

      el.style.width = origW; el.style.height = origH; el.style.overflow = origO;
      viewport.style.transform = origT;

      const link = document.createElement('a');
      link.download = `${(filename || 'script').replace(/\.aef$/i, '')}-flow-diagram.png`;
      link.href = dataUrl; link.click();
    } catch (err) { console.error('Export failed:', err); }
    finally { setExporting(false); }
  }, [filename]);

  return (
    <Stack gap="sm">
      <Group justify="flex-end" gap="xs">
        <Button size="xs" variant={locked ? 'filled' : 'outline'} color="teal" radius="md" onClick={() => setLocked(l => !l)}>
          {locked ? 'Locked' : 'Unlocked'}
        </Button>
        <Button size="xs" variant="outline" color="teal" radius="md"
          leftSection={exporting ? <Loader size={10} color="teal" /> : <IconDownload size={13} />}
          disabled={exporting} onClick={exportPng}>
          Export PNG
        </Button>
      </Group>
      <Box ref={diagramRef} style={{ height: 500, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8, overflow: 'hidden' }}>
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes} nodesDraggable={!locked} onInit={onInit}
          fitView fitViewOptions={{ padding: 0.15 }}
          minZoom={0.1} maxZoom={2.5} attributionPosition="bottom-right">
          <Controls />
          <MiniMap nodeColor={n => NODE_COLORS[n.data?.nodeType]?.border || '#999'} zoomable pannable />
          <Background color="#e8e8e8" gap={20} />
        </ReactFlow>
      </Box>
      <Text size="xs" c="dimmed" ta="center">
        Use scroll to zoom · Drag to pan · {locked ? 'Click "Unlocked" to move nodes' : 'Click "Locked" to lock positions'}
      </Text>
    </Stack>
  );
}

function ScriptFlowDiagram(props) {
  return (
    <ReactFlowProvider>
      <ScriptFlowDiagramInner {...props} />
    </ReactFlowProvider>
  );
}

// ============================================================================
// Inner component (needs ReactFlowProvider wrapper)
// ============================================================================

function UccxScriptTranslatorInner({ onBackToPlatformSelector, onBackToHome }) {
  const { getApiToken } = useAuth();
  const fileInputRef = useRef(null);

  // ---- Single file mode state ----
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [extractedStrings, setExtractedStrings] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // ---- Batch mode state ----
  const [batchMode, setBatchMode] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]); // [{ id, file, status, analysis, meta, error }]
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [synthesis, setSynthesis] = useState(null);
  const [synthesizing, setSynthesizing] = useState(false);
  const [batchActiveTab, setBatchActiveTab] = useState('summary');

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [diagramLocked, setDiagramLocked] = useState(true);
  const [exportingDiagram, setExportingDiagram] = useState(false);
  const diagramRef = useRef(null);
  const { fitView } = useReactFlow();

  // ---- File handling ----

  const handleFiles = useCallback((fileList) => {
    const aefFiles = Array.from(fileList).filter(f => f.name.toLowerCase().endsWith('.aef'));
    if (aefFiles.length === 0) { setError('Please upload .aef script files.'); return; }
    setError(null);

    if (aefFiles.length === 1) {
      setBatchMode(false);
      setBatchFiles([]);
      setSynthesis(null);
      setFile(aefFiles[0]);
      setAnalysis(null);
      setExtractedStrings(null);
      setNodes([]);
      setEdges([]);
    } else {
      setBatchMode(true);
      setFile(null);
      setAnalysis(null);
      setSynthesis(null);
      setBatchFiles(aefFiles.map(f => ({ id: f.name, file: f, status: 'pending', analysis: null, meta: null, error: null })));
      setBatchActiveTab('summary');
    }
  }, [setNodes, setEdges]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const onDragOver = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const onDragLeave = useCallback(() => setDragging(false), []);

  const onFileInputChange = useCallback((e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = '';
  }, [handleFiles]);

  // ---- Analyze ----

  const runAnalysis = async ({ strings, filename, previousAnalysis = null }) => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = await getApiToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (e) { console.warn('Failed to get API token:', e); }
    const res = await fetch(`${API_BASE}/ccutils/uccx/translate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ extractedStrings: strings, filename, previousAnalysis }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || `Analysis failed (${res.status})`);
    return { analysis: data.analysis, meta: data.meta || null };
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setAnalysis(null);
    setExtractedStrings(null);
    setNodes([]);
    setEdges([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const strings = extractStrings(arrayBuffer);

      if (strings.length < 50) {
        throw new Error('Could not extract readable content from this file. Ensure it is a valid UCCX .aef script.');
      }

      setExtractedStrings(strings);
      const { analysis: result, meta } = await runAnalysis({ strings, filename: file.name });
      setAnalysis({ ...result, merge_notes: null });
      setAnalysisMeta(meta);

      if (result.call_flow_steps?.length > 0) {
        const { nodes: ln, edges: le } = buildFlowGraph(result);
        setNodes(ln);
        setEdges(le);
        setActiveTab('overview');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async () => {
    if (!extractedStrings || !analysis) return;
    setReanalyzing(true);
    setError(null);

    try {
      const { analysis: result, meta } = await runAnalysis({ strings: extractedStrings, filename: file?.name, previousAnalysis: analysis });
      setAnalysis(result);
      setAnalysisMeta(meta);

      if (result.call_flow_steps?.length > 0) {
        const { nodes: ln, edges: le } = buildFlowGraph(result);
        setNodes(ln);
        setEdges(le);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setReanalyzing(false);
    }
  };

  // ---- Batch analyze ----

  const handleBatchAnalyze = async () => {
    if (batchFiles.length === 0) return;
    setBatchAnalyzing(true);
    setSynthesis(null);
    setBatchProgress({ done: 0, total: batchFiles.length });

    // Reset all to pending
    setBatchFiles(prev => prev.map(f => ({ ...f, status: 'pending', analysis: null, meta: null, error: null })));

    const CONCURRENCY = 3;
    const queue = [...batchFiles];
    let done = 0;
    const results = {};

    const runOne = async (item) => {
      setBatchFiles(prev => prev.map(f => f.id === item.id ? { ...f, status: 'analyzing' } : f));
      try {
        const arrayBuffer = await item.file.arrayBuffer();
        const strings = extractStrings(arrayBuffer);
        if (strings.length < 50) throw new Error('Could not extract readable content.');

        const batchHeaders = { 'Content-Type': 'application/json' };
        try {
          const token = await getApiToken();
          if (token) batchHeaders['Authorization'] = `Bearer ${token}`;
        } catch (e) { console.warn('Failed to get API token:', e); }
        const res = await fetch(`${API_BASE}/ccutils/uccx/translate`, {
          method: 'POST',
          headers: batchHeaders,
          body: JSON.stringify({ extractedStrings: strings, filename: item.file.name }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || `Analysis failed (${res.status})`);

        results[item.id] = data.analysis;
        done++;
        setBatchProgress({ done, total: batchFiles.length });
        const graph = data.analysis.call_flow_steps?.length > 0 ? buildFlowGraph(data.analysis) : { nodes: [], edges: [] };
        setBatchFiles(prev => prev.map(f => f.id === item.id
          ? { ...f, status: 'done', analysis: data.analysis, meta: data.meta || null, nodes: graph.nodes, edges: graph.edges }
          : f));
      } catch (err) {
        done++;
        setBatchProgress({ done, total: batchFiles.length });
        setBatchFiles(prev => prev.map(f => f.id === item.id
          ? { ...f, status: 'error', error: err.message }
          : f));
      }
    };

    // Process with concurrency limit
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (item) await runOne(item);
      }
    });
    await Promise.all(workers);
    setBatchAnalyzing(false);

    // Auto-synthesize if 2+ succeeded
    const successful = Object.entries(results).map(([filename, analysis]) => ({ filename, analysis }));
    if (successful.length >= 2) {
      setSynthesizing(true);
      setBatchActiveTab('summary');
      try {
        const synthHeaders = { 'Content-Type': 'application/json' };
        try {
          const token = await getApiToken();
          if (token) synthHeaders['Authorization'] = `Bearer ${token}`;
        } catch (e) { console.warn('Failed to get API token:', e); }
        const res = await fetch(`${API_BASE}/ccutils/uccx/synthesize`, {
          method: 'POST',
          headers: synthHeaders,
          body: JSON.stringify({ scripts: successful }),
        });
        const data = await res.json();
        if (res.ok && data.success) setSynthesis(data.synthesis);
      } catch (err) {
        console.error('Synthesis failed:', err);
      } finally {
        setSynthesizing(false);
      }
    }
  };

  // ---- Export diagram as PNG ----

  const exportDiagram = useCallback(async () => {
    if (!diagramRef.current) return;
    setExportingDiagram(true);
    try {
      const { toPng } = await import('html-to-image');
      const padding = 40;
      const reactFlowEl = diagramRef.current;
      const viewport = reactFlowEl.querySelector('.react-flow__viewport');
      if (!viewport) return;

      // Calculate bounding box from actual node positions
      const nodeEls = reactFlowEl.querySelectorAll('.react-flow__node');
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      nodeEls.forEach(el => {
        const rect = el.getBoundingClientRect();
        const parentRect = reactFlowEl.getBoundingClientRect();
        const x = rect.left - parentRect.left;
        const y = rect.top - parentRect.top;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + rect.width);
        maxY = Math.max(maxY, y + rect.height);
      });

      const totalWidth = maxX - minX + padding * 2;
      const totalHeight = maxY - minY + padding * 2;
      const originalTransform = viewport.style.transform;
      const originalWidth = reactFlowEl.style.width;
      const originalHeight = reactFlowEl.style.height;
      const originalOverflow = reactFlowEl.style.overflow;

      // Temporarily expand container and reset viewport
      reactFlowEl.style.width = `${totalWidth}px`;
      reactFlowEl.style.height = `${totalHeight}px`;
      reactFlowEl.style.overflow = 'visible';
      viewport.style.transform = `translate(${-minX + padding}px, ${-minY + padding}px) scale(1)`;

      await new Promise(r => setTimeout(r, 500));

      const dataUrl = await toPng(reactFlowEl, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        width: totalWidth,
        height: totalHeight,
        cacheBust: true,
        filter: (node) => {
          if (node.classList) {
            return !node.classList.contains('react-flow__controls') &&
                   !node.classList.contains('react-flow__minimap') &&
                   !node.classList.contains('react-flow__attribution');
          }
          return true;
        },
      });

      // Restore original styles
      reactFlowEl.style.width = originalWidth;
      reactFlowEl.style.height = originalHeight;
      reactFlowEl.style.overflow = originalOverflow;
      viewport.style.transform = originalTransform;

      const basename = file?.name?.replace(/\.aef$/i, '') || 'uccx-script';
      const link = document.createElement('a');
      link.download = `${basename}-flow-diagram.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportingDiagram(false);
    }
  }, [file]);

  // ---- Render helpers ----

  const formatBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`;

  return (
    <Box style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0ca678 0%, #099268 100%)', padding: '40px 20px' }}>
      <Container size="xl">

        {/* Top-left navigation: exit to platform selector + return to the CC Utils hub */}
        <PlatformNavBar
          onBackToPlatformSelector={onBackToPlatformSelector}
          onBackToHome={onBackToHome}
          showHome={!!onBackToHome}
          homeLabel="Contact Center Utilities"
          onGradient
          mb="lg"
        />

        {/* Header */}
        <Stack align="center" gap="xs" mb="xl">
          <ThemeIcon size={64} radius="xl" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}>
            <IconCode size={36} style={{ color: 'white' }} />
          </ThemeIcon>
          <Title order={1} size="2.2rem" fw={700} style={{ color: 'white', textAlign: 'center' }}>
            UCCX Script Translator
          </Title>
          <Text size="md" style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', maxWidth: 520 }}>
            Upload one .aef file for a single analysis with flow diagram, or drop multiple files for batch analysis with cross-script synthesis.
          </Text>
        </Stack>

        {/* Upload card */}
        <Paper shadow="xl" radius="lg" p="xl" mb="lg" style={{ background: 'white' }}>
          <Stack gap="lg">

            {!API_BASE && (
              <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" radius="md">
                <Text size="sm"><strong>API not configured.</strong> Set <code>REACT_APP_CCUTILS_API_BASE_URL</code> in your environment.</Text>
              </Alert>
            )}

            {/* Drop zone */}
            <Box
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-gray-4)'}`,
                borderRadius: 12,
                padding: '36px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                background: dragging ? 'var(--mantine-color-teal-0)' : 'var(--mantine-color-gray-0)',
                transition: 'all 0.2s ease',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".aef"
                multiple
                style={{ display: 'none' }}
                onChange={onFileInputChange}
              />
              <Stack align="center" gap="sm">
                <ThemeIcon size={48} radius="xl" color={dragging ? 'teal' : 'gray'} variant="light">
                  <IconUpload size={26} />
                </ThemeIcon>
                <Text fw={600} size="md" c={dragging ? 'teal' : 'dark'}>
                  {dragging ? 'Drop .aef files here' : 'Drop .aef files here or click to browse'}
                </Text>
                <Text size="sm" c="dimmed">Single file: analysis + flow diagram · Multiple files: batch analysis + synthesis</Text>
              </Stack>
            </Box>

            {/* Single file info */}
            {!batchMode && file && (
              <Paper withBorder radius="md" p="md" style={{ background: 'var(--mantine-color-teal-0)' }}>
                <Group gap="sm">
                  <ThemeIcon size="lg" radius="md" color="teal" variant="light">
                    <IconFileCode size={18} />
                  </ThemeIcon>
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Text size="sm" fw={600}>{file.name}</Text>
                    <Text size="xs" c="dimmed">{formatBytes(file.size)}</Text>
                  </Stack>
                  {analysis && <ThemeIcon size="lg" radius="md" color="green" variant="light"><IconCheck size={18} /></ThemeIcon>}
                </Group>
              </Paper>
            )}

            {/* Batch file list */}
            {batchMode && batchFiles.length > 0 && (
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={600} c="dimmed">{batchFiles.length} scripts queued</Text>
                  {batchAnalyzing && (
                    <Text size="sm" c="teal" fw={600}>{batchProgress.done} of {batchProgress.total} analyzed…</Text>
                  )}
                </Group>
                <ScrollArea.Autosize mah={220}>
                  <Stack gap={6}>
                    {batchFiles.map(f => (
                      <Paper key={f.id} withBorder radius="md" p="sm">
                        <Group gap="sm">
                          <ThemeIcon size="sm" radius="sm" color={
                            f.status === 'done' ? 'green' : f.status === 'error' ? 'red' : f.status === 'analyzing' ? 'teal' : 'gray'
                          } variant="light">
                            {f.status === 'done' ? <IconCircleCheck size={14} /> :
                             f.status === 'error' ? <IconCircleX size={14} /> :
                             f.status === 'analyzing' ? <Loader size={10} color="teal" /> :
                             <IconClockHour4 size={14} />}
                          </ThemeIcon>
                          <Text size="sm" style={{ flex: 1 }} fw={500}>{f.id}</Text>
                          {f.error && <Text size="xs" c="red">{f.error}</Text>}
                          {!batchAnalyzing && f.status === 'pending' && (
                            <IconX size={14} style={{ cursor: 'pointer', color: 'var(--mantine-color-gray-5)' }}
                              onClick={() => setBatchFiles(prev => prev.filter(x => x.id !== f.id))} />
                          )}
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </Stack>
            )}

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md">
                <Text size="sm">{error}</Text>
              </Alert>
            )}

            {!batchMode ? (
              <Button size="lg" color="teal" radius="md"
                leftSection={loading ? <Loader size="xs" color="white" /> : <IconCode size={20} />}
                disabled={!file || loading || !API_BASE}
                onClick={handleAnalyze} style={{ fontWeight: 600 }}>
                {loading ? 'Analyzing Script…' : 'Analyze Script'}
              </Button>
            ) : (
              <Button size="lg" color="teal" radius="md"
                leftSection={batchAnalyzing ? <Loader size="xs" color="white" /> : <IconFiles size={20} />}
                disabled={batchFiles.length < 2 || batchAnalyzing || synthesizing || !API_BASE}
                onClick={handleBatchAnalyze} style={{ fontWeight: 600 }}>
                {batchAnalyzing ? `Analyzing ${batchProgress.done} of ${batchProgress.total}…` :
                 synthesizing ? 'Synthesizing findings…' :
                 `Analyze All ${batchFiles.length} Scripts`}
              </Button>
            )}

          </Stack>
        </Paper>

        {/* Results */}
        {analysis && (
          <Paper shadow="xl" radius="lg" p="xl" style={{ background: 'white' }}>
            <Stack gap="lg">

              {/* Company + script type header */}
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Title order={3} size="1.3rem" fw={700}>
                    {analysis.company_name || 'UCCX Script Analysis'}
                  </Title>
                  <Group gap="xs">
                    <Badge color="teal" variant="light">{analysis.script_type || 'Inbound IVR'}</Badge>
                    {analysis.queues?.length > 0 && (
                      <Badge color="blue" variant="light">{analysis.queues.length} Queue{analysis.queues.length !== 1 ? 's' : ''}</Badge>
                    )}
                    {analysis.menu_options?.length > 0 && (
                      <Badge color="violet" variant="light">{analysis.menu_options.length} Menu Options</Badge>
                    )}
                  </Group>
                </Stack>
                <Group gap="sm">
                  <Button
                    size="xs"
                    variant="outline"
                    color="teal"
                    radius="md"
                    leftSection={reanalyzing ? <Loader size={12} color="teal" /> : <IconRefresh size={14} />}
                    disabled={reanalyzing || !extractedStrings}
                    onClick={handleReanalyze}
                  >
                    {reanalyzing ? 'Reanalyzing…' : 'Reanalyze'}
                  </Button>
                  <Badge color="green" variant="filled" size="lg">Analysis Complete</Badge>
                </Group>
              </Group>

              {analysisMeta && (
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    Analyzed in {(analysisMeta.duration_ms / 1000).toFixed(1)}s
                    {analysisMeta.input_tokens != null && ` · ${analysisMeta.input_tokens.toLocaleString()} input tokens`}
                    {analysisMeta.output_tokens != null && ` · ${analysisMeta.output_tokens.toLocaleString()} output tokens`}
                  </Text>
                </Group>
              )}

              {analysis.merge_notes && (
                <Alert icon={<IconRefresh size={16} />} color="teal" variant="light" radius="md">
                  <Text size="sm" fw={600} mb={2}>Merged Analysis</Text>
                  <Text size="sm">{analysis.merge_notes}</Text>
                </Alert>
              )}

              <Divider />

              <Tabs value={activeTab} onChange={setActiveTab} color="teal">
                <Tabs.List>
                  <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>Overview</Tabs.Tab>
                  <Tabs.Tab value="diagram" leftSection={<IconChartDots size={14} />}>Flow Diagram</Tabs.Tab>
                  {analysis.menu_options?.length > 0 && (
                    <Tabs.Tab value="menu" leftSection={<IconList size={14} />}>IVR Menu</Tabs.Tab>
                  )}
                  {analysis.variables?.length > 0 && (
                    <Tabs.Tab value="variables" leftSection={<IconVariable size={14} />}>Variables</Tabs.Tab>
                  )}
                  <Tabs.Tab value="technical" leftSection={<IconSettings size={14} />}>Technical Details</Tabs.Tab>
                </Tabs.List>

                {/* ---- Overview ---- */}
                <Tabs.Panel value="overview" pt="lg">
                  <Stack gap="lg">
                    <Paper withBorder radius="md" p="lg">
                      <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Summary</Text>
                      <Text size="sm" style={{ lineHeight: 1.7 }}>{analysis.overview}</Text>
                    </Paper>

                    {analysis.features?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>Features Detected</Text>
                        <Group gap="sm" wrap="wrap">
                          {analysis.features.map((f, i) => (
                            <Badge key={i} color={FEATURE_COLORS[i % FEATURE_COLORS.length]} variant="light" size="md">
                              {f}
                            </Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {analysis.call_flow_steps?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>Call Flow Steps</Text>
                        <Stack gap="sm">
                          {analysis.call_flow_steps.map((step) => {
                            const colors = NODE_COLORS[step.node_type] || NODE_COLORS.action;
                            return (
                              <Paper key={step.step} withBorder radius="md" p="md" style={{ borderLeft: `4px solid ${colors.border}`, background: colors.bg }}>
                                <Group gap="sm" align="flex-start">
                                  <Badge size="lg" variant="filled" style={{ background: colors.border, minWidth: 28 }}>{step.step}</Badge>
                                  <Box style={{ flex: 1 }}>
                                    <Text size="sm" fw={700} c={colors.text}>{step.title}</Text>
                                    <Text size="xs" c="dimmed" mt={2} style={{ lineHeight: 1.5 }}>{step.description}</Text>
                                  </Box>
                                  <Badge size="xs" variant="outline" style={{ borderColor: colors.border, color: colors.text }}>
                                    {(NODE_COLORS[step.node_type] || NODE_COLORS.action).label}
                                  </Badge>
                                </Group>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}

                    {analysis.analysis_notes && (
                      <Alert icon={<IconInfoCircle size={16} />} color="teal" variant="light" radius="md">
                        <Text size="sm" fw={600} mb={4}>Analyst Notes</Text>
                        <Text size="sm" style={{ lineHeight: 1.6 }}>{analysis.analysis_notes}</Text>
                      </Alert>
                    )}
                  </Stack>
                </Tabs.Panel>

                {/* ---- Flow Diagram ---- */}
                <Tabs.Panel value="diagram" pt="lg">
                  <Stack gap="md">
                    <Group justify="space-between" align="center">
                      <Group gap="xs" wrap="wrap">
                        {Object.entries(NODE_COLORS).map(([type, colors]) => (
                          <Badge
                            key={type}
                            size="sm"
                            variant="outline"
                            style={{ borderColor: colors.border, color: colors.text, background: colors.bg }}
                          >
                            {colors.label}
                          </Badge>
                        ))}
                      </Group>
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant={diagramLocked ? 'filled' : 'outline'}
                          color="teal"
                          radius="md"
                          onClick={() => setDiagramLocked(l => !l)}
                        >
                          {diagramLocked ? 'Locked' : 'Unlocked'}
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          color="teal"
                          radius="md"
                          leftSection={exportingDiagram ? <Loader size={10} color="teal" /> : <IconDownload size={13} />}
                          disabled={exportingDiagram}
                          onClick={exportDiagram}
                        >
                          Export PNG
                        </Button>
                      </Group>
                    </Group>
                    <Box ref={diagramRef} style={{ height: 560, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8, overflow: 'hidden' }}>
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={nodeTypes}
                        nodesDraggable={!diagramLocked}
                        fitView
                        fitViewOptions={{ padding: 0.15 }}
                        minZoom={0.1}
                        maxZoom={2.5}
                        attributionPosition="bottom-right"
                      >
                        <Controls />
                        <MiniMap nodeColor={(n) => NODE_COLORS[n.data?.nodeType]?.border || '#999'} zoomable pannable />
                        <Background color="#e8e8e8" gap={20} />
                      </ReactFlow>
                    </Box>
                    <Text size="xs" c="dimmed" ta="center">
                      Use scroll to zoom · Drag to pan · {diagramLocked ? 'Click "Unlocked" to move nodes' : 'Click "Locked" to lock node positions'} · Dashed lines indicate overflow/fallback paths
                    </Text>
                  </Stack>
                </Tabs.Panel>

                {/* ---- IVR Menu ---- */}
                {analysis.menu_options?.length > 0 && (
                  <Tabs.Panel value="menu" pt="lg">
                    <ScrollArea>
                      <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 80 }}>Digit</Table.Th>
                            <Table.Th>Department / Option</Table.Th>
                            <Table.Th>Prompt Variable</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {analysis.menu_options.map((opt, i) => (
                            <Table.Tr key={i}>
                              <Table.Td>
                                <Badge size="lg" color="blue" variant="filled">{opt.digit || '—'}</Badge>
                              </Table.Td>
                              <Table.Td><Text size="sm" fw={500}>{opt.department}</Text></Table.Td>
                              <Table.Td>
                                <Text size="sm" c="dimmed" style={{ fontFamily: 'monospace' }}>
                                  {opt.prompt_variable || '—'}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Tabs.Panel>
                )}

                {/* ---- Variables ---- */}
                {analysis.variables?.length > 0 && (
                  <Tabs.Panel value="variables" pt="lg">
                    <ScrollArea>
                      <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Variable Name</Table.Th>
                            <Table.Th style={{ width: 120 }}>Type</Table.Th>
                            <Table.Th>Purpose</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {analysis.variables.map((v, i) => (
                            <Table.Tr key={i}>
                              <Table.Td>
                                <Text size="sm" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v.name}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  size="sm"
                                  color={v.type === 'Boolean' ? 'orange' : v.type === 'Integer' ? 'blue' : 'teal'}
                                  variant="light"
                                >
                                  {v.type}
                                </Badge>
                              </Table.Td>
                              <Table.Td><Text size="sm">{v.purpose}</Text></Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Tabs.Panel>
                )}

                {/* ---- Technical Details ---- */}
                <Tabs.Panel value="technical" pt="lg">
                  <Stack gap="lg">

                    {analysis.queues?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Contact Service Queues (CSQs)</Text>
                        <Group gap="sm" wrap="wrap">
                          {analysis.queues.map((q, i) => (
                            <Badge key={i} color="teal" variant="light" size="md" style={{ fontFamily: 'monospace' }}>{q}</Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {analysis.subflows?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Subflows Referenced</Text>
                        <Group gap="sm" wrap="wrap">
                          {analysis.subflows.map((s, i) => (
                            <Badge key={i} color="violet" variant="light" size="md" style={{ fontFamily: 'monospace' }}>{s}</Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {analysis.audio_prompts?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Audio Prompt Variables</Text>
                        <Group gap="xs" wrap="wrap">
                          {analysis.audio_prompts.map((p, i) => (
                            <Badge key={i} color="gray" variant="outline" size="sm" style={{ fontFamily: 'monospace' }}>{p}</Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {analysis.email_addresses?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Email Addresses</Text>
                        <Group gap="sm" wrap="wrap">
                          {analysis.email_addresses.map((e, i) => (
                            <Badge key={i} color="blue" variant="light" size="md">{e}</Badge>
                          ))}
                        </Group>
                      </Box>
                    )}

                    {analysis.api_calls?.length > 0 && (
                      <Box>
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>API / Web Service Calls</Text>
                        <Stack gap="sm">
                          {analysis.api_calls.map((call, i) => (
                            <Paper key={i} withBorder radius="md" p="md">
                              <Group gap="sm" mb={6} align="center">
                                <Badge
                                  size="md"
                                  color={
                                    call.method === 'POST' ? 'blue' :
                                    call.method === 'GET' ? 'green' :
                                    call.method === 'PUT' ? 'orange' :
                                    call.method === 'DELETE' ? 'red' :
                                    call.method === 'SOAP' ? 'violet' : 'gray'
                                  }
                                  variant="filled"
                                  style={{ fontFamily: 'monospace', minWidth: 54, textAlign: 'center' }}
                                >
                                  {call.method || 'HTTP'}
                                </Badge>
                                <Text size="sm" style={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}>
                                  {call.url}
                                </Text>
                              </Group>
                              {call.purpose && (
                                <Text size="xs" c="dimmed" mb={call.parameters?.length ? 6 : 0}>{call.purpose}</Text>
                              )}
                              {call.parameters?.length > 0 && (
                                <Group gap="xs" wrap="wrap">
                                  <Text size="xs" fw={600} c="dimmed">Params:</Text>
                                  {call.parameters.map((p, pi) => (
                                    <Badge key={pi} size="xs" color="gray" variant="outline" style={{ fontFamily: 'monospace' }}>{p}</Badge>
                                  ))}
                                </Group>
                              )}
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {(!analysis.queues?.length && !analysis.subflows?.length && !analysis.audio_prompts?.length && !analysis.email_addresses?.length && !analysis.api_calls?.length) && (
                      <Text size="sm" c="dimmed" ta="center" py="xl">No additional technical details extracted.</Text>
                    )}

                  </Stack>
                </Tabs.Panel>

              </Tabs>
            </Stack>
          </Paper>
        )}

        {/* ---- Batch Results ---- */}
        {batchMode && (synthesis || batchFiles.some(f => f.status === 'done')) && (
          <Paper shadow="xl" radius="lg" p="xl" style={{ background: 'white' }}>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Stack gap={2}>
                  <Title order={3} size="1.3rem" fw={700}>Batch Analysis Results</Title>
                  <Text size="sm" c="dimmed">
                    {batchFiles.filter(f => f.status === 'done').length} of {batchFiles.length} scripts analyzed
                    {synthesizing && ' · Synthesizing cross-script findings…'}
                  </Text>
                </Stack>
                <Group gap="xs">
                  <Badge color="green" variant="filled">{batchFiles.filter(f => f.status === 'done').length} Done</Badge>
                  {batchFiles.filter(f => f.status === 'error').length > 0 && (
                    <Badge color="red" variant="filled">{batchFiles.filter(f => f.status === 'error').length} Failed</Badge>
                  )}
                </Group>
              </Group>

              <Divider />

              <Tabs value={batchActiveTab} onChange={setBatchActiveTab} color="teal">
                <Tabs.List style={{ flexWrap: 'wrap' }}>
                  <Tabs.Tab value="summary" leftSection={<IconNetwork size={14} />}>
                    {synthesizing ? <Group gap={6}><Loader size={10} />Synthesizing…</Group> : 'Summary'}
                  </Tabs.Tab>
                  {batchFiles.filter(f => f.status === 'done').map(f => (
                    <Tabs.Tab key={f.id} value={f.id} leftSection={<IconFileCode size={14} />}>
                      {f.id.replace(/\.aef$/i, '')}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>

                {/* Summary Tab */}
                <Tabs.Panel value="summary" pt="lg">
                  {synthesizing && (
                    <Group justify="center" py="xl">
                      <Loader color="teal" />
                      <Text c="dimmed">Analyzing cross-script relationships…</Text>
                    </Group>
                  )}
                  {!synthesizing && !synthesis && (
                    <Text c="dimmed" ta="center" py="xl">Synthesis will appear here once all scripts are analyzed.</Text>
                  )}
                  {synthesis && (
                    <Stack gap="lg">
                      {/* Executive Summary */}
                      <Paper withBorder radius="md" p="lg">
                        <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Executive Summary</Text>
                        <Text size="sm" style={{ lineHeight: 1.7 }}>{synthesis.executive_summary}</Text>
                      </Paper>

                      {/* Script Inventory */}
                      {synthesis.script_inventory?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>Script Inventory</Text>
                          <ScrollArea>
                            <Table striped highlightOnHover withTableBorder withColumnBorders>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Filename</Table.Th>
                                  <Table.Th>Type</Table.Th>
                                  <Table.Th>Department</Table.Th>
                                  <Table.Th>Primary Queue</Table.Th>
                                  <Table.Th>Purpose</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {synthesis.script_inventory.map((s, i) => (
                                  <Table.Tr key={i}>
                                    <Table.Td><Text size="xs" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{s.filename}</Text></Table.Td>
                                    <Table.Td><Badge size="sm" color={s.script_type === 'Main IVR' ? 'teal' : s.script_type === 'Subflow' ? 'violet' : s.script_type === 'Callback' ? 'orange' : 'gray'} variant="light">{s.script_type}</Badge></Table.Td>
                                    <Table.Td><Text size="xs">{s.department || '—'}</Text></Table.Td>
                                    <Table.Td><Text size="xs" style={{ fontFamily: 'monospace' }}>{s.primary_queue || '—'}</Text></Table.Td>
                                    <Table.Td><Text size="xs">{s.purpose}</Text></Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </ScrollArea>
                        </Box>
                      )}

                      {/* Departments */}
                      {synthesis.departments?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>Departments & Functions</Text>
                          <Stack gap="sm">
                            {synthesis.departments.map((dept, i) => (
                              <Paper key={i} withBorder radius="md" p="md" style={{ borderLeft: '4px solid var(--mantine-color-teal-6)' }}>
                                <Group justify="space-between" mb={6}>
                                  <Group gap="xs">
                                    <IconBuildingStore size={16} color="var(--mantine-color-teal-6)" />
                                    <Text size="sm" fw={700}>{dept.name}</Text>
                                  </Group>
                                  <Group gap="xs">
                                    {dept.queues?.map((q, qi) => <Badge key={qi} size="xs" color="blue" variant="light" style={{ fontFamily: 'monospace' }}>{q}</Badge>)}
                                  </Group>
                                </Group>
                                <Text size="xs" c="dimmed" mb={6}>{dept.description}</Text>
                                <Group gap="xs" wrap="wrap">
                                  {dept.scripts?.map((s, si) => <Badge key={si} size="xs" color="gray" variant="outline" style={{ fontFamily: 'monospace' }}>{s}</Badge>)}
                                </Group>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Shared Components */}
                      {synthesis.shared_components?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>Shared Components</Text>
                          <Stack gap="sm">
                            {synthesis.shared_components.map((c, i) => (
                              <Paper key={i} withBorder radius="md" p="md">
                                <Group justify="space-between" mb={4}>
                                  <Group gap="xs">
                                    <IconComponents size={15} color="var(--mantine-color-violet-6)" />
                                    <Text size="sm" fw={600} style={{ fontFamily: 'monospace' }}>{c.filename}</Text>
                                    <Badge size="xs" color="violet" variant="light">{c.component_type}</Badge>
                                  </Group>
                                  <Text size="xs" c="dimmed">Used by {c.used_by?.length || 0} scripts</Text>
                                </Group>
                                <Text size="xs" c="dimmed" mb={6}>{c.purpose}</Text>
                                <Group gap="xs" wrap="wrap">
                                  {c.used_by?.map((s, si) => <Badge key={si} size="xs" color="gray" variant="outline" style={{ fontFamily: 'monospace' }}>{s}</Badge>)}
                                </Group>
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Common Patterns + All Queues */}
                      <Group gap="xl" align="flex-start">
                        {synthesis.common_patterns?.length > 0 && (
                          <Box style={{ flex: 1 }}>
                            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Common Patterns</Text>
                            <Group gap="xs" wrap="wrap">
                              {synthesis.common_patterns.map((p, i) => (
                                <Badge key={i} color={FEATURE_COLORS[i % FEATURE_COLORS.length]} variant="light" size="md">{p}</Badge>
                              ))}
                            </Group>
                          </Box>
                        )}
                        {synthesis.all_queues?.length > 0 && (
                          <Box style={{ flex: 1 }}>
                            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>All Queues ({synthesis.all_queues.length})</Text>
                            <Group gap="xs" wrap="wrap">
                              {synthesis.all_queues.map((q, i) => (
                                <Badge key={i} color="blue" variant="light" size="sm" style={{ fontFamily: 'monospace' }}>{q}</Badge>
                              ))}
                            </Group>
                          </Box>
                        )}
                      </Group>

                      {/* Architecture Notes */}
                      {synthesis.architecture_notes && (
                        <Alert icon={<IconInfoCircle size={16} />} color="teal" variant="light" radius="md">
                          <Text size="sm" fw={600} mb={4}>Architecture Notes</Text>
                          <Text size="sm" style={{ lineHeight: 1.6 }}>{synthesis.architecture_notes}</Text>
                        </Alert>
                      )}
                    </Stack>
                  )}
                </Tabs.Panel>

                {/* Per-script tabs (text only) */}
                {batchFiles.filter(f => f.status === 'done').map(f => (
                  <Tabs.Panel key={f.id} value={f.id} pt="lg">
                    <Stack gap="lg">
                      {/* Overview */}
                      <Paper withBorder radius="md" p="lg">
                        <Group justify="space-between" mb={8}>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px' }}>Summary</Text>
                          <Group gap="xs">
                            <Badge color="teal" variant="light">{f.analysis.script_type || 'Inbound IVR'}</Badge>
                            {f.analysis.company_name && <Badge color="gray" variant="outline">{f.analysis.company_name}</Badge>}
                          </Group>
                        </Group>
                        <Text size="sm" style={{ lineHeight: 1.7 }}>{f.analysis.overview}</Text>
                      </Paper>

                      {/* Features */}
                      {f.analysis.features?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Features</Text>
                          <Group gap="xs" wrap="wrap">
                            {f.analysis.features.map((feat, i) => (
                              <Badge key={i} color={FEATURE_COLORS[i % FEATURE_COLORS.length]} variant="light">{feat}</Badge>
                            ))}
                          </Group>
                        </Box>
                      )}

                      {/* Call Flow Steps */}
                      {f.analysis.call_flow_steps?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>Call Flow Steps</Text>
                          <Stack gap="sm">
                            {f.analysis.call_flow_steps.map(step => {
                              const colors = NODE_COLORS[step.node_type] || NODE_COLORS.action;
                              return (
                                <Paper key={step.step} withBorder radius="md" p="sm" style={{ borderLeft: `4px solid ${colors.border}`, background: colors.bg }}>
                                  <Group gap="sm" align="flex-start">
                                    <Badge size="md" variant="filled" style={{ background: colors.border, minWidth: 26 }}>{step.step}</Badge>
                                    <Box style={{ flex: 1 }}>
                                      <Text size="sm" fw={700} c={colors.text}>{step.title}</Text>
                                      <Text size="xs" c="dimmed" mt={2}>{step.description}</Text>
                                    </Box>
                                    <Badge size="xs" variant="outline" style={{ borderColor: colors.border, color: colors.text }}>{colors.label}</Badge>
                                  </Group>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      {/* Queues + Subflows + Emails */}
                      <Group gap="xl" align="flex-start">
                        {f.analysis.queues?.length > 0 && (
                          <Box>
                            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: '0.5px' }}>Queues</Text>
                            <Group gap="xs" wrap="wrap">
                              {f.analysis.queues.map((q, i) => <Badge key={i} color="teal" variant="light" style={{ fontFamily: 'monospace' }}>{q}</Badge>)}
                            </Group>
                          </Box>
                        )}
                        {f.analysis.subflows?.length > 0 && (
                          <Box>
                            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: '0.5px' }}>Subflows</Text>
                            <Group gap="xs" wrap="wrap">
                              {f.analysis.subflows.map((s, i) => <Badge key={i} color="violet" variant="light" style={{ fontFamily: 'monospace' }}>{s}</Badge>)}
                            </Group>
                          </Box>
                        )}
                        {f.analysis.email_addresses?.length > 0 && (
                          <Box>
                            <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: '0.5px' }}>Emails</Text>
                            <Group gap="xs" wrap="wrap">
                              {f.analysis.email_addresses.map((e, i) => <Badge key={i} color="blue" variant="light">{e}</Badge>)}
                            </Group>
                          </Box>
                        )}
                      </Group>

                      {/* Variables */}
                      {f.analysis.variables?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>Variables</Text>
                          <ScrollArea>
                            <Table striped withTableBorder withColumnBorders>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Name</Table.Th>
                                  <Table.Th>Type</Table.Th>
                                  <Table.Th>Purpose</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {f.analysis.variables.map((v, i) => (
                                  <Table.Tr key={i}>
                                    <Table.Td><Text size="xs" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v.name}</Text></Table.Td>
                                    <Table.Td><Badge size="xs" color={v.type === 'Boolean' ? 'orange' : v.type === 'Integer' ? 'blue' : 'teal'} variant="light">{v.type}</Badge></Table.Td>
                                    <Table.Td><Text size="xs">{v.purpose}</Text></Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </ScrollArea>
                        </Box>
                      )}

                      {/* API Calls */}
                      {f.analysis.api_calls?.length > 0 && (
                        <Box>
                          <Text size="sm" fw={600} c="dimmed" tt="uppercase" mb={8} style={{ letterSpacing: '0.5px' }}>API Calls</Text>
                          <Stack gap="sm">
                            {f.analysis.api_calls.map((call, i) => (
                              <Paper key={i} withBorder radius="md" p="sm">
                                <Group gap="sm" mb={4}>
                                  <Badge size="sm" color={call.method === 'POST' ? 'blue' : call.method === 'GET' ? 'green' : call.method === 'SOAP' ? 'violet' : 'gray'} variant="filled" style={{ fontFamily: 'monospace' }}>{call.method || 'HTTP'}</Badge>
                                  <Text size="xs" style={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}>{call.url}</Text>
                                </Group>
                                {call.purpose && <Text size="xs" c="dimmed">{call.purpose}</Text>}
                              </Paper>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* Flow Diagram */}
                      {f.nodes?.length > 0 && (
                        <Box>
                          <Group gap="xs" mb={10}>
                            {Object.entries(NODE_COLORS).map(([type, colors]) => (
                              <Badge key={type} size="sm" variant="outline"
                                style={{ borderColor: colors.border, color: colors.text, background: colors.bg }}>
                                {colors.label}
                              </Badge>
                            ))}
                          </Group>
                          <ScriptFlowDiagram nodes={f.nodes} edges={f.edges} filename={f.id} />
                        </Box>
                      )}

                      {f.meta && (
                        <Text size="xs" c="dimmed">
                          Analyzed in {(f.meta.duration_ms / 1000).toFixed(1)}s
                          {f.meta.input_tokens != null && ` · ${f.meta.input_tokens.toLocaleString()} input tokens`}
                          {f.meta.output_tokens != null && ` · ${f.meta.output_tokens.toLocaleString()} output tokens`}
                        </Text>
                      )}
                    </Stack>
                  </Tabs.Panel>
                ))}
              </Tabs>
            </Stack>
          </Paper>
        )}

        <Text size="xs" style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 24 }}>
          Powered by Amazon Bedrock Nova Pro · Presidio Networked Solutions, Inc.
        </Text>

      </Container>
    </Box>
  );
}

// ============================================================================
// Exported component wrapped in ReactFlowProvider
// ============================================================================

function UccxScriptTranslator(props) {
  return (
    <ReactFlowProvider>
      <UccxScriptTranslatorInner {...props} />
    </ReactFlowProvider>
  );
}

export default UccxScriptTranslator;
